import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import config from '../config';
import Cookies from 'js-cookie';
import { getMediaUrl } from '../utils/urlHelpers'; // Fixed import to use getMediaUrl
import Notification from '../components/Notification';
import CustomCarousel from '../components/CustomCarousel';
import PremiumCarousel from '../components/PremiumCarousel';
import ProductsGrid from '../components/ProductsGrid';
import ProductFilters from '../components/ProductFilters';
import { UserContext } from '../contexts/UserContext';
import { CategoryContext } from '../contexts/CategoryContext';
import { CityContext } from '../contexts/CityContext';
import '../styles/categoryProductsPage.css';
import { CarouselSkeleton, ProductGridSkeleton } from '../components/Skeletons';
import PerformanceIndicator from '../components/PerformanceIndicator';
import { PerformanceToggle } from '../components/PerformanceDashboard';
import { usePerformanceMonitoring } from '../hooks/usePerformanceMonitoring';
import ProductService from '../services/productService';
// Add EmptyState component
const EmptyState = ({ category, onAddPostClick }) => (
    <div className="empty-state-container">
        <div className="empty-state-illustration">
            <i className="fas fa-box-open"></i>
        </div>
        <h2 className="empty-state-title">
            Aucun produit trouvé dans {category} 🤔
        </h2>
        <p className="empty-state-message">
            Soyez le premier à publier dans cette catégorie!
            Partagez vos trésors avec la communauté.
        </p>
        <button onClick={onAddPostClick} className="add-post-cta">
            <i className="fas fa-plus"></i>
            Publier une annonce
        </button>
    </div>
);

const fetchProductsByMainCategory = async (categoryId) => {
    try {
        const response = await fetch(`${config.backendUrl}/products/main-categories-products/view/${categoryId}/`);
        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            console.error('Failed to fetch products by main category');
            return { results: [] };
        }
    } catch (error) {
        console.error('An error occurred while fetching products by main category:', error);
        return { results: [] };
    }
};

// New function to fetch promoted products by main category
const fetchPromotedProductsByMainCategory = async (mainCategoryId) => {
    try {
        const response = await fetch(`${config.backendUrl}/products/promoted/main-category/${mainCategoryId}/`);
        if (response.ok) {
            const data = await response.json();
            return data.results || [];
        } else {
            console.error('Failed to fetch promoted products by main category');
            return [];
        }
    } catch (error) {
        console.error('An error occurred while fetching promoted products by main category:', error);
        return [];
    }
};

const fetchCategoriesByMainCategory = async (mainCategoryId) => {
    try {
        const response = await fetch(`${config.backendUrl}/products/main-category/categories/view/${mainCategoryId}/`);
        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            console.error('Failed to fetch categories by main category');
            return [];
        }
    } catch (error) {
        console.error('An error occurred while fetching categories by main category:', error);
        return [];
    }
};

const CategoryProductsPage = ({ onLoginClick }) => {
    const { categoryId, categoryName: urlCategoryName } = useParams(); // Get both ID and name from URL
    const navigate = useNavigate();
    const { user: loggedInUser } = useContext(UserContext);
    const { categoryList } = useContext(CategoryContext);
    const { cities } = useContext(CityContext);
    const [products, setProducts] = useState([]);
    const [categoryName, setCategoryName] = useState(urlCategoryName || ''); // Use URL name or fallback
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [promotedProducts, setPromotedProducts] = useState([]);
    const [mainCategoryPromotedProducts, setMainCategoryPromotedProducts] = useState([]);
    const [mainCategoryProducts, setMainCategoryProducts] = useState({ results: [] });
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState({ message: '', type: 'info', header: '' });
    
    // Filter state
    const [filters, setFilters] = useState({
        sortBy: '',
        condition: '',
        minPrice: '',
        maxPrice: '',
        city: '',
        negotiable: false,
        promotedOnly: false
    });
    const [allProducts, setAllProducts] = useState([]); // Store unfiltered products
    const [filteredProducts, setFilteredProducts] = useState([]); // Store filtered products
    
    const pageSize = 10;
    
    // Performance monitoring
    const { startTiming, endTiming, recordPageLoad } = usePerformanceMonitoring();
    const [performanceData, setPerformanceData] = useState({ loadTime: 0, fromCache: false, itemCount: 0 });

    // Add mounted state to prevent rendering before component is fully ready
    const [isMounted, setIsMounted] = useState(false);

    // Ensure component is mounted before rendering
    useEffect(() => {
        setIsMounted(true);
        return () => setIsMounted(false);
    }, []);

    // Filter products based on current filters
    const applyFilters = useCallback((productsToFilter, currentFilters) => {
        let filtered = [...productsToFilter];

        // Condition filter
        if (currentFilters.condition) {
            filtered = filtered.filter(product => 
                product.condition && product.condition.toLowerCase() === currentFilters.condition.toLowerCase()
            );
        }

        // Price range filter
        if (currentFilters.minPrice || currentFilters.maxPrice) {
            filtered = filtered.filter(product => {
                const price = parseFloat(product.price) || 0;
                const min = parseFloat(currentFilters.minPrice) || 0;
                const max = parseFloat(currentFilters.maxPrice) || Infinity;
                return price >= min && (max === Infinity || price <= max);
            });
        }

        // City filter
        if (currentFilters.city) {
            filtered = filtered.filter(product => 
                product.city_name && product.city_name.toLowerCase() === currentFilters.city.toLowerCase()
            );
        }

        // Negotiable filter
        if (currentFilters.negotiable) {
            filtered = filtered.filter(product => product.negotiable === true);
        }

        // Promoted only filter
        if (currentFilters.promotedOnly) {
            filtered = filtered.filter(product => product.promoted === true);
        }

        // Sort products
        if (currentFilters.sortBy) {
            switch (currentFilters.sortBy) {
                case 'newest':
                    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                    break;
                case 'oldest':
                    filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                    break;
                case 'price_low':
                    filtered.sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0));
                    break;
                case 'price_high':
                    filtered.sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0));
                    break;
                case 'most_liked':
                    filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0));
                    break;
                case 'most_viewed':
                    filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
                    break;
                default:
                    break;
            }
        }

        return filtered;
    }, []);

    // Handle filter changes
    const handleFiltersChange = useCallback((newFilters) => {
        setFilters(newFilters);
    }, []);

    // Apply filters when filters or allProducts change
    useEffect(() => {
        if (allProducts.length > 0) {
            const filtered = applyFilters(allProducts, filters);
            setFilteredProducts(filtered);
            setProducts(filtered); // Update products displayed
        }
    }, [filters, allProducts, applyFilters]);

    // Function to get category name from ID or use URL name
    const getCategoryName = (categoryId) => {
        // If we have URL category name, use it (URL-decoded)
        if (urlCategoryName) {
            return decodeURIComponent(urlCategoryName);
        }
        // Otherwise, look it up in categoryList
        if (!categoryList || categoryList.length === 0) return categoryId;
        const foundCategory = categoryList.find(cat => cat.id == categoryId);
        return foundCategory ? foundCategory.name : categoryId;
    };

    // Update category name when categoryList or categoryId changes
    useEffect(() => {
        const name = getCategoryName(categoryId);
        setCategoryName(name);
    }, [categoryList, categoryId, urlCategoryName]);

    const updateUserPreferences = async (categoryId) => {
        if (!loggedInUser) return; // Only update preferences for logged-in users

        try {
            const token = Cookies.get('authToken');
            await fetch(`${config.backendUrl}/users/preferences/categories/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Token ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ category_ids: [categoryId] }),
            });

        } catch (error) {
            console.error('Failed to update user preferences:', error);
        }
    };

    useEffect(() => {
        const getProducts = async () => {

            // Validate category ID parameter
            if (!categoryId || isNaN(parseInt(categoryId)) || parseInt(categoryId) <= 0) {
                console.error('CategoryProductsPage: Invalid category ID:', categoryId);
                setNotification({
                    message: 'ID de catégorie invalide.',
                    type: 'error',
                    header: 'Erreur de paramètre'
                });
                setLoading(false);
                navigate('/dashboard');
                return;
            }
            
            setLoading(true);
            const pageStartTime = startTiming('page_load');
            const loadStartTime = startTiming('category_products');
            
            try {
                // Always use category ID since it's now in the path parameter
                const result = await ProductService.getProductsByCategoryIdCombined(categoryId, page, pageSize);
                
                const loadTime = endTiming(result.fromCache, result.products?.length || 0);
                
                // Transform products to ensure proper image structure
                const transformedProducts = (result.products || []).map(product => {
                    // Ensure first_image is available for ProductsGrid
                    if (!product.first_image && product.media && product.media.length > 0) {
                        product.first_image = product.media[0].url;
                    }
                    // Ensure images array is available as fallback
                    if (!product.images && product.media && product.media.length > 0) {
                        product.images = product.media.map(item => ({
                            url: item.url,
                            image: item.url
                        }));
                    }
                    return product;
                });
                
                setProducts(transformedProducts);
                setAllProducts(transformedProducts); // Store unfiltered products
                setFilteredProducts(transformedProducts); // Initialize filtered products
                setPromotedProducts(result.promotedProducts || []);
                setHasMore(result.hasMore || false);
                
                setPerformanceData({
                    loadTime,
                    fromCache: result.fromCache,
                    itemCount: result.products?.length || 0
                });

                // Get main category data if we have products
                if (result.products && result.products.length > 0) {
                    const mainCategoryId = result.products[0].main_category_id;
                    const mainCategoryProductsData = await fetchProductsByMainCategory(mainCategoryId);
                    
                    // Fetch promoted products by main category
                    const mainCategoryPromoted = await fetchPromotedProductsByMainCategory(mainCategoryId);
                    setMainCategoryPromotedProducts(mainCategoryPromoted);
                    
                    // Transform main category products as well
                    const transformedMainProducts = (mainCategoryProductsData.results || []).map(product => {
                        // Ensure first_image is available for ProductsGrid
                        if (!product.first_image && product.media && product.media.length > 0) {
                            product.first_image = product.media[0].url;
                        }
                        // Ensure images array is available as fallback
                        if (!product.images && product.media && product.media.length > 0) {
                            product.images = product.media.map(item => ({
                                url: item.url,
                                image: item.url
                            }));
                        }
                        return product;
                    });
                    
                    setMainCategoryProducts({
                        ...mainCategoryProductsData,
                        results: transformedMainProducts
                    });

                    const categoriesData = await fetchCategoriesByMainCategory(mainCategoryId);
                    setCategories(categoriesData.filter(cat => cat.id != categoryId));
                    // Update user preferences for the category
                    updateUserPreferences(mainCategoryId);
                }
            } catch (error) {
                console.error('Error loading category products:', error);
                endTiming(false, 0, error);
                setProducts([]);
                setPromotedProducts([]);
                setMainCategoryPromotedProducts([]);
                setHasMore(false);
            } finally {
                setLoading(false);
                const totalPageTime = endTiming(false, 0);
                recordPageLoad('CategoryProducts', totalPageTime);
            }
        };

        getProducts();
    }, [categoryId, page, pageSize, startTiming, endTiming, recordPageLoad]);

    const fetchMoreData = async () => {
        const nextPage = page + 1;
        const loadStartTime = startTiming('pagination');
        
        try {
            const result = await ProductService.getProductsByCategoryIdCombined(categoryId, nextPage, pageSize);
            const loadTime = endTiming(result.fromCache, result.products?.length || 0);

            // Filter out duplicate products based on their `id` and transform them
            const transformedNewProducts = (result.products || []).map(product => {
                // Ensure first_image is available for ProductsGrid
                if (!product.first_image && product.media && product.media.length > 0) {
                    product.first_image = product.media[0].url;
                }
                // Ensure images array is available as fallback
                if (!product.images && product.media && product.media.length > 0) {
                    product.images = product.media.map(item => ({
                        url: item.url,
                        image: item.url
                    }));
                }
                return product;
            });

            const uniqueProducts = [
                ...allProducts,
                ...transformedNewProducts.filter(
                    (newProduct) => !allProducts.some((existingProduct) => existingProduct.id === newProduct.id)
                ),
            ];

            setAllProducts(uniqueProducts); // Update unfiltered products
            // filteredProducts will be updated by the useEffect
            setPage(nextPage);
            setHasMore(result.hasMore || false);
        } catch (error) {
            console.error('Error loading more category products:', error);
            endTiming(false, 0, error);
        }
    };

    const handleLike = async (productId, isLikedByUser) => {
        if (!loggedInUser) {
            setNotification({ message: 'Vous devez être connecté pour aimer ou ne pas aimer un produit.', type: 'info', header: 'Connecter vous!' });
            return;
        }

        try {
            const token = Cookies.get('authToken');
            const url = `${config.backendUrl}/products/${isLikedByUser ? 'unlike' : 'like'}/${productId}/`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    Authorization: `Token ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setProducts((prevProducts) => {
                    return prevProducts.map((product) => {
                        if (product.id === productId) {
                            return {
                                ...product,
                                likes: data.likes,
                                liked_by: data.liked
                                    ? [...(product.liked_by || []), loggedInUser.id]
                                    : (product.liked_by || []).filter(id => id !== loggedInUser.id),
                            };
                        }
                        return product;
                    });
                });
                setNotification({ message: `Produit ${isLikedByUser ? 'non aimé' : 'aimé'} avec succès!`, type: 'success', header: 'Confirmation' });
            } else {
                console.error(`Échec de ${isLikedByUser ? 'ne pas aimer' : 'aimer'} le produit`);
                setNotification({ message: `Échec de ${isLikedByUser ? 'ne pas aimer' : 'aimer'} le produit.`, type: 'error', header: 'Erreur' });
            }
        } catch (error) {
            console.error(`Une erreur s'est produite lors de ${isLikedByUser ? 'ne pas aimer' : 'aimer'} le produit:`, error);
            setNotification({ message: `Une erreur s'est produite lors de ${isLikedByUser ? 'ne pas aimer' : 'aimer'} le produit.`, type: 'error', header: 'Erreur' });
        }
    };

    const handleNotificationClose = () => {
        setNotification({ message: '', type: 'info', header: '' });
    };

    const handleProductClick = (productUuid, event) => {  // Changed parameter name for clarity, added event
        // Add animation effect
        const card = event?.currentTarget;
        if (card) {
            card.classList.add('clicked');
            setTimeout(() => {
                card.classList.remove('clicked');
            }, 600);
        }
        navigate(`/products/view/${productUuid}`);  // Navigate using UUID
    };

    const handleAddPostClick = () => {
        if (!loggedInUser) {
            sessionStorage.setItem('postRedirectUrl', '/add-post');
            setNotification({
                message: 'Connectez-vous pour publier une annonce',
                type: 'info',
                header: 'Connection requise'
            });
            onLoginClick();
        } else {
            navigate('/add-post');
        }
    };

    const filteredMainCategoryProducts = mainCategoryProducts.results.filter(
        mainProduct => !products.some(product => product.id === mainProduct.id)
    );

    // Early return with loading state if not mounted or still loading initial data
    // This prevents white screen while ensuring navbar and footer render
    if (!isMounted || (loading && products.length === 0)) {
        return (
            <div className="category-products-page-container">
                <div className="page-loading-container" style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    <CarouselSkeleton />
                    <ProductGridSkeleton />
                    <ProductGridSkeleton />
                </div>
            </div>
        );
    }

    return (
        <div className="category-products-page-container">{/* <PerformanceIndicator
                loadTime={performanceData.loadTime}
                fromCache={performanceData.fromCache}
                itemCount={performanceData.itemCount}
                type="category products"
            /> */}
            {loading ? (
                <CarouselSkeleton /> // Show skeleton for promoted products
            ) : promotedProducts.length > 0 && (
                <PremiumCarousel
                    products={promotedProducts}
                    onProductClick={handleProductClick}
                    handleLike={handleLike}
                    loggedInUser={loggedInUser}
                    title="Produits Sponsorisés"
                    subtitle="Découvrez les meilleurs produits de cette catégorie"
                />
            )}

            {/* Main Category Promoted Products */}
            {!loading && mainCategoryPromotedProducts.length > 0 && (
                <PremiumCarousel
                    products={mainCategoryPromotedProducts}
                    onProductClick={handleProductClick}
                    handleLike={handleLike}
                    loggedInUser={loggedInUser}
                    title="Produits Sponsorisés - Catégorie Principale"
                    subtitle="Découvrez d'autres produits sponsorisés dans cette catégorie principale"
                />
            )}

            {/* Add Product Filters */}
            {!loading && allProducts.length > 0 && (
                <ProductFilters
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    productsCount={filteredProducts.length}
                    totalProducts={allProducts.length}
                />
            )}

            {loading ? (
                <ProductGridSkeleton /> // Show skeleton for products grid
            ) : allProducts.length === 0 ? (
                <EmptyState
                    category={categoryName}
                    onAddPostClick={handleAddPostClick}
                />
            ) : filteredProducts.length === 0 ? (
                <div className="no-results-container" style={{ textAlign: 'center', padding: '2rem' }}>
                    <div className="no-results-illustration">
                        <i className="fas fa-search" style={{ fontSize: '3rem', color: '#ccc', marginBottom: '1rem' }}></i>
                    </div>
                    <h3>Aucun produit ne correspond à vos filtres</h3>
                    <p style={{ color: '#666', marginBottom: '1rem' }}>
                        Essayez d'ajuster vos critères de recherche pour voir plus de résultats.
                    </p>
                </div>
            ) : (
                <div className="main-content">
                    <h2>{categoryName}</h2>
                    <InfiniteScroll
                        dataLength={products.length}
                        next={fetchMoreData}
                        hasMore={hasMore}
                    >
                        <div className="products-grid-container">
                            <ProductsGrid
                                products={products}
                                likedProducts={products.filter(product =>
                                    product.liked_by &&
                                    product.liked_by.includes(loggedInUser?.id)
                                ).map(product => product.id)}
                                loggedInUser={loggedInUser}
                                handleProductClick={handleProductClick}
                                handleLikeClick={handleLike}
                                hasMore={hasMore}
                                fetchMoreData={fetchMoreData}
                                loading={loading}
                            />
                        </div>
                    </InfiniteScroll>
                </div>
            )}

            {!hasMore && filteredMainCategoryProducts.length > 0 && (
                <div className="related-content">
                    <h2>Produits similaires</h2>
                    <div className="products-grid-container">
                        <ProductsGrid
                            products={filteredMainCategoryProducts}
                            likedProducts={filteredMainCategoryProducts.filter(product =>
                                product.liked_by &&
                                product.liked_by.includes(loggedInUser?.id)
                            ).map(product => product.id)}
                            loggedInUser={loggedInUser}
                            handleProductClick={handleProductClick}
                            handleLikeClick={handleLike}
                            hasMore={false}
                            fetchMoreData={() => { }}
                            loading={false}
                        />
                    </div>
                </div>
            )}

            {categories.length > 0 && (
                <div className="top-categories-section">
                    <h2>Catégories similaires</h2>
                    <div className="top-categories-container">
                        {categories.map((cat) => (
                            <div
                                key={cat.id}
                                className="top-category-item"
                                onClick={() => navigate(`/products/category/${cat.id}/${encodeURIComponent(cat.name)}`)}
                            >                            <div className="category-icon-container">
                                {cat.icon ? (
                                    <img
                                        src={getMediaUrl(cat.icon)}
                                        alt={cat.name}
                                        className="top-category-image"
                                    />
                                ) : (
                                    <div className="category-initials-container">
                                        <p className="category-initials">
                                            {cat.name.charAt(0).toUpperCase()}
                                        </p>
                                    </div>
                                )}
                            </div>
                                <p className="top-category-name">{cat.name}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onClose={handleNotificationClose}
                />
            )}
            <PerformanceToggle />
        </div>
    );
};

export default CategoryProductsPage;